import _ from 'lodash';
import URL from 'url';
import qs from 'qs';
import appConfig from '../../../appConfig/AppConfig';
const config = appConfig();

/**
 * Simple URL builder
 * @param  {String} fullDomain The full domain including protocol
 * @param  {String} pathName The path in the url
 * @param  {Array} queryParams Array of query param objects
 * @return {String} The built url
 */
export function buildUrl(fullDomain, pathName, queryParams) {
  let url = '';

  if (fullDomain) {
    const builtDomain = fullDomain ? fullDomain.replace(/[\/\\]+$/, '') : '';
    const builtPathName = pathName ? pathName.replace(/^[\/\\]+/, '') : '';
    const queryParamStrings = [];

    _.each(queryParams, paramObj => {
      if (paramObj.key && paramObj.value) {
        queryParamStrings.push(`${paramObj.key}=${paramObj.value}`);
      }
    });

    const queryParamSeparator = queryParamStrings.length > 0 ? '?' : '';

    url = `${builtDomain}/${builtPathName}${queryParamSeparator}${queryParamStrings.join(
      '&'
    )}`;
  }

  return url;
}

/**
 * Constructs a maps address link
 * @param  {String} address Address
 * @return {String} Formatted google maps address URL
 */
export function getMapUrl(address) {
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${address}`.replace(
      /\s/g,
      '+'
    );
  }
  return '';
}

/**
 * updates window history
 * @param  {Object} window            window object
 * @param  {Object} location          react router location object
 * @param  {string} layout            guestbook or obit
 * @param  {boolean} allowGuestBook   is guestbook allowed
 * @param  {number} pid               person id
 * @param  {number} page              current page number
 * @return {undefined}                returns nothing
 */
export function updateHistory(
  window,
  location,
  layout,
  allowGuestBook,
  pid,
  page
) {
  const {location: url, history} = window;
  const {pathname, query} = location;
  const params = _.clone(_.omit(query, ['view', 'page']));

  if (history) {
    const urlConfig = {
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port,
      pathname,
    };

    if (!params.pid && pid) {
      params.pid = pid;
    }

    if (allowGuestBook) {
      if (layout !== 'Obit') {
        params.view = 'guestbook';
      }
      if (page) {
        params.page = page;
      }
    }

    urlConfig.query = params;

    const updatedUrl = URL.format(urlConfig);

    history.replaceState(null, null, updatedUrl);
  }
}

/**
 * @param  {String} value String for formatting funeral, person name
 * @return {String} Formatted funeral, person name
 */
export function formatNameString(value) {
  let string = '';

  if (value) {
    string = value
      .replace(/[^A-Za-z-]/g, ' ')
      .replace(/\s+/g, '-')
      .toLowerCase();
  }
  return string;
}

/**
 * @param  {Object} options Options for building a flowers URL
 * @return {String} Formatted flowers URL
 */
export function buildObitFlowersUrl(options = {}) {
  const defaultFlowerSettings = {
    nodeMobilePersonLinkOverrideUrl: '',
    nodeDesktopPersonLinkOverrideUrl: '',
  };
  const cultureCode = options.cultureCode || 'en-us';
  const flowerSetting =
    (options.flowerSettings &&
      options.flowerSettings.find(
        setting => setting.cultureCode === cultureCode
      )) ||
    defaultFlowerSettings;
  const affiliateId = options.affiliateId || '0';
  const personName = options.personName || '';
  const personId = options.personId || '0';
  const isMobile = options.isMobile || false;
  const flowersUrl = isMobile
    ? flowerSetting.nodeMobilePersonLinkOverrideUrl
    : flowerSetting.nodeDesktopPersonLinkOverrideUrl;

  return flowersUrl
    .replace('##pid##', personId)
    .replace('##pn##', formatNameString(personName))
    .replace('##affiliateid##', affiliateId);
}

/**
 * @param  {Object} options Options for building a flowers URL
 * @return {String} Formatted flowers URL
 */
export function buildFuneralHomeFlowersUrl(options = {}) {
  const defaultFlowerSettings = {
    nodeMobileLinkOverrideUrl: '',
    nodeDesktopLinkOverrideUrl: '',
  };
  const cultureCode = options.cultureCode || 'en-us';
  const flowerSetting =
    (options.flowerSettings &&
      options.flowerSettings.find(
        setting => setting.cultureCode === cultureCode
      )) ||
    defaultFlowerSettings;
  const funeralHomeId = options.funeralHomeId || '0';
  const affiliateId = options.affiliateId || '0';
  const funeralHomeName = options.funeralHomeName || '';
  const isMobile = options.isMobile || false;
  const flowersUrl = isMobile
    ? flowerSetting.nodeMobileLinkOverrideUrl
    : flowerSetting.nodeDesktopLinkOverrideUrl;

  return flowersUrl
    .replace('##fhid##', funeralHomeId)
    .replace('##fhn##', formatNameString(funeralHomeName))
    .replace('##affiliateid##', affiliateId);
}

/**
 * @param  {String} sitename affiliate sitename
 * @return {String} Formatted affiliate url
 */
export function buildAffiliateUrl(sitename) {
  const host = config.vanityDomain;
  const endpoint = `obituaries/${sitename}`;

  return buildUrl(host, endpoint);
}

/**
 * Returns a URL with a protocol. JSON LD Requires a valid url so
 * just www.domain.com doesn't fly.
 * @param {string} url url URL from the service
 * @return {string} Full url
 */
export function ensureProtocol(url) {
  if (!url) {
    return '';
  }

  // has protocol, send it along
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // No protocol provided, add http: to be safe
  // NOTE: this will handle both domain.com and www.domain.com which might
  // not be desired
  return `http://${url}`;
}

/**
 * Add a query param to provided url
 * @param {string} urlToDecorate - Url to be decorated
 * @param {Object} queryParams - query name value pairs
 * @returns {string} Decorated Url
 */
export function addQueryParams(urlToDecorate, queryParams) {
  const urlParts = urlToDecorate.split('?');
  let parsed = qs.parse(urlParts[1]);

  parsed = _.merge(parsed, queryParams || {});
  urlParts[1] = qs.stringify(parsed);

  return _.isEmpty(urlParts[1]) ? urlParts[0] : urlParts.join('?');
}



// WEBPACK FOOTER //
// ./src/reactModules/shared/utilities/urlHelpers.js